import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";
import SCHEDULE_LIST_MUTATION from "../../constants/mutationKey";
import {
  getCurrentTimelineQuery,
  invalidateTimelineRelatedQueries,
} from "../../utils/fast";
import useFASTScheduleWebSocket from "../useFASTScheduleWebSocket";
import useTimebounds from "../useTimebounds";
import useTimelineItems from "../useTimelineItems";

import type {
  Query,
  QueryClient,
  UseMutationResult,
} from "@tanstack/react-query";

import type {
  GetScheduleAllScheduleListResponse,
  UpdateScheduleListMediaResponse,
} from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";

export const optimisticUpdate = ({
  mediaId,
  startTime,
  queryKey,
  queryClient,
}: {
  mediaId: string;
  startTime: string;
  queryKey: Query<GetScheduleAllScheduleListResponse>["queryKey"];
  queryClient: QueryClient;
}): {
  previousScheduleList?: GetScheduleAllScheduleListResponse;
} => {
  const previousScheduleList: GetScheduleAllScheduleListResponse | undefined =
    queryClient.getQueryData(queryKey);

  const updatedList = previousScheduleList?.data.map((scheduleItem) => {
    if (scheduleItem.uuid === mediaId) {
      return {
        ...scheduleItem,
        start_time: startTime,
        end_time: moment(startTime)
          .add(scheduleItem.object_total_duration, "milliseconds")
          .toISOString(),
      };
    }
    return scheduleItem;
  });

  if (previousScheduleList) {
    queryClient.setQueryData(queryKey, {
      ...previousScheduleList,
      data: updatedList,
    });
  }

  return { previousScheduleList };
};

interface MediaToUpdate {
  uuid: string;
  startTime: string;
  endTime?: string;
} // endTime is mandatory only for optimistic updates

function useUpdateScheduleListMediaMutation(): UseMutationResult<
  UpdateScheduleListMediaResponse,
  unknown,
  MediaToUpdate
> {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");
  const queryClient = useQueryClient();

  const { currentDay } = useTimebounds();
  const { confirmUpdatedSchedule } = useFASTScheduleWebSocket();

  const { timelineContent } = useTimelineItems();

  const updateScheduleListMediaMutation = useMutation({
    mutationKey: [SCHEDULE_LIST_MUTATION, channelId],
    mutationFn: async (mediaToUpdate: MediaToUpdate) => {
      const currentMedia = timelineContent[mediaToUpdate.uuid];

      if (!currentMedia) {
        throw new Error("Media not found");
      }

      return FastAPI.updateScheduleListMedia({
        channelId,
        scheduleId: currentMedia.scheduleId,
        uuid: mediaToUpdate.uuid,
        media: { start_time: mediaToUpdate.startTime },
      });
    },
    onMutate: async ({ uuid, startTime }) => {
      const { currentQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });
      await queryClient.cancelQueries(currentQuery);

      const { previousScheduleList } = optimisticUpdate({
        mediaId: uuid,
        startTime,
        queryKey: currentQuery,
        queryClient,
      });

      return { previousScheduleList };
    },
    onSuccess: (_, media): void => {
      const currentMedia = timelineContent[media.uuid];

      if (!currentMedia) {
        throw new Error("Media not found");
      }

      invalidateTimelineRelatedQueries({
        currentDay,
        channelId,
        queryClient,
        scheduleId: currentMedia.scheduleId,
      });
      queryClient.invalidateQueries({
        queryKey: ["getScheduleVariantDetails"],
      });

      confirmUpdatedSchedule(currentDay);

      toast.success(t("programUpdateSuccess"));
    },
    onError: (error, _, context): void => {
      const { currentQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });

      queryClient.setQueryData(currentQuery, context?.previousScheduleList);
      console.error(error);

      toast.error(t("programUpdateErrorNoReason"));
    },
    onSettled: (): void => {
      const { previousQuery, currentQuery, nextQuery } =
        getCurrentTimelineQuery({
          currentDay,
          channelId,
        });
      queryClient.invalidateQueries(previousQuery);
      queryClient.invalidateQueries(currentQuery);
      queryClient.invalidateQueries(nextQuery);
    },
  });

  return updateScheduleListMediaMutation;
}

export default useUpdateScheduleListMediaMutation;
